import { apiClient } from "./client";
import { PayloadFormat } from "../utils/storage";
import { toBoqVersionKey, type BoqVersionRoutePrefix } from "../utils/boqVersionPrefix";

// BillOfQuantitiesRequest extends PayloadFormat directly (no nesting)
export interface BillOfQuantitiesRequest extends PayloadFormat {
  // Additional fields can be added here if needed
}

export interface BillOfQuantitiesResponse {
  success: boolean;
  data: PayloadFormat | null;
  message?: string;
}

export const billOfQuantitiesApi = {
  get: async (
    params?: BillOfQuantitiesRequest,
  ): Promise<BillOfQuantitiesResponse> => {
    const response = await apiClient.get("/v3/billofquantities", { params });
    return response.data;
  },

  getByProject: async (
    projectName: string,
    oppNo?: string,
  ): Promise<BillOfQuantitiesResponse> => {
    const response = await apiClient.get(
      `/v3/billofquantities/${encodeURIComponent(projectName)}`,
      oppNo ? { params: { oppNo } } : undefined,
    );
    return response.data;
  },

  create: async (
    data: BillOfQuantitiesRequest,
    oppNo?: string,
  ): Promise<BillOfQuantitiesResponse> => {
    const response = await apiClient.post(
      "/v3/billofquantities",
      data,
      oppNo ? { params: { oppNo } } : undefined,
    );
    return response.data;
  },

  update: async (
    id: string,
    data: BillOfQuantitiesRequest,
    oppNo?: string,
  ): Promise<BillOfQuantitiesResponse> => {
    const response = await apiClient.put(
      `/v3/billofquantities/${id}`,
      data,
      oppNo ? { params: { oppNo } } : undefined,
    );
    return response.data;
  },

  delete: async (id: string): Promise<BillOfQuantitiesResponse> => {
    const response = await apiClient.delete(`/v3/billofquantities/${id}`);
    return response.data;
  },

  deleteByProject: async (
    projectName: string,
    oppNo?: string,
  ): Promise<BillOfQuantitiesResponse> => {
    const response = await apiClient.delete(
      `/v3/billofquantities/${encodeURIComponent(projectName)}`,
      oppNo ? { params: { oppNo } } : undefined,
    );
    return response.data;
  },

  // Get opportunities from Business Central
  getOpportunities: async (): Promise<any> => {
    const response = await apiClient.get("/bc/opportunities");
    return response.data;
  },

  // Create BOQ version (called simultaneously with BOQ submission)
  createVersion: async (
    data: BillOfQuantitiesRequest,
    oppNo?: string,
  ): Promise<BillOfQuantitiesResponse> => {
    const raw = data as unknown as Record<string, unknown>;
    const payload =
      raw?.version !== undefined
        ? { ...raw, version: toBoqVersionKey(raw.version as number | string) }
        : raw;
    const response = await apiClient.post(
      "/v3/boq-versions",
      payload,
      oppNo ? { params: { oppNo } } : undefined,
    );
    return response.data;
  },

  // Get version history for a project
  getVersionHistory: async (
    projectName: string,
    oppNo?: string,
  ): Promise<{
    projectName: string;
    currentVersion: number;
    totalVersions: number;
    versions: {
      version: number | string;
      rateVersion: number;
      createdBy: string;
      createdAt: string;
      changeLog: string;
      snapshot: {
        tenderTotal: number;
        roadAndSewersTotal: number;
        groundworksTotal: number;
        status: string;
        plotCount: number;
        lastModifiedBy: string;
        rateCardSnapshot: any;
      };
    }[];
  }> => {
    const response = await apiClient.get(
      `/v3/boq-versions/${encodeURIComponent(projectName)}`,
      oppNo ? { params: { oppNo } } : undefined,
    );
    return response.data;
  },

  // Get specific version of BOQ
  getBoqVersion: async (
    projectName: string,
    version: number | string,
    oppNo?: string,
    // Pages nested under /roads-sewers or /groundworks leave this undefined
    // and get route-based prefix detection. Callers that aren't route-scoped
    // (e.g. MainLayout.tsx, mounted on every page, including ones outside
    // both workflow routes) must pass this explicitly, or a plain numeric
    // `version` is GET as a bare number instead of GWKS-N/RS-N.
    versionPrefixOverride?: BoqVersionRoutePrefix | null,
  ): Promise<{
    changeLog: string;
    createdAt: string;
    createdBy: string;
    projectName: string;
    rateVersion: number;
    version: number | string;
    snapshot: {
      tenderTotal: number;
      roadAndSewersTotal: number;
      groundworksTotal: number;
      status: string;
      plotCount: number;
      lastModifiedBy: string;
      rateCardSnapshot: any;
    };
    boq: any;
  }> => {
    const versionKey = toBoqVersionKey(version, versionPrefixOverride);
    const response = await apiClient.get(
      `/v3/boq-versions/${encodeURIComponent(projectName)}/${versionKey}`,
      oppNo ? { params: { oppNo } } : undefined,
    );
    return response.data;
  },

  // Update specific version of BOQ
  updateVersion: async (
    projectName: string,
    version: number | string,
    data: BillOfQuantitiesRequest,
    oppNo?: string,
    // Pages nested under /roads-sewers or /groundworks leave this undefined
    // and get route-based prefix detection. Callers that aren't route-scoped
    // (e.g. /tender/import-takeoff's per-job-type import handlers) must pass
    // this explicitly, or a plain numeric `version` is PUT as a bare number
    // instead of GWKS-N/RS-N — see toBoqVersionKey.
    versionPrefixOverride?: BoqVersionRoutePrefix | null,
  ): Promise<BillOfQuantitiesResponse> => {
    const versionKey = toBoqVersionKey(version, versionPrefixOverride);
    const payload = { ...(data as unknown as Record<string, unknown>), version: versionKey };
    const response = await apiClient.put(
      `/v3/boq-versions/${encodeURIComponent(projectName)}/${versionKey}`,
      payload,
      oppNo ? { params: { oppNo } } : undefined,
    );
    return response.data;
  },

  // Delete specific version of BOQ
  deleteVersion: async (
    projectName: string,
    version: number | string,
    oppNo?: string,
  ): Promise<BillOfQuantitiesResponse> => {
    const versionKey = toBoqVersionKey(version);
    const response = await apiClient.delete(
      `/v3/boq-versions/${encodeURIComponent(projectName)}/${versionKey}`,
      oppNo ? { params: { oppNo } } : undefined,
    );
    return response.data;
  },
};
